import React, { Children } from 'react';
import { motion } from 'framer-motion';
import { MessageSquareWarningIcon, BanknoteIcon, HelpCircleIcon, SplitIcon } from 'lucide-react';
const painPoints = [{
  icon: BanknoteIcon,
  title: 'Chasing people is awkward',
  description: '"Any chance you can send that £20?" Sent three times. Still nothing.'
}, {
  icon: MessageSquareWarningIcon,
  title: 'Group chats become chaos',
  description: 'Screenshots, three payment apps, and nobody knows who paid what.'
}, {
  icon: HelpCircleIcon,
  title: 'Someone always forgets',
  description: '"I\'ll send it later." Weeks pass. You absorb the cost. Again.'
}, {
  icon: SplitIcon,
  title: 'It kills the vibe',
  description: "A great night shouldn't end with a spreadsheet. Money shouldn't make things weird."
}];
const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 16
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5
    }
  }
};
export function ProblemSection() {
  return <section className="relative py-20 lg:py-28 bg-gray-950 text-white overflow-hidden" data-id="element-220" data-mp="obookpn0ezgx">
      {/* Subtle texture */}
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(251,191,36,0.06),transparent_50%)]" data-id="element-221" data-mp="scqq5d4efnm2" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-222" data-mp="x632anrw61xx">
        {/* Section header */}
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true,
        margin: '-80px'
      }} transition={{
        duration: 0.5
      }} className="text-center max-w-2xl mx-auto mb-16" data-id="element-223" data-mp="123360s19qslv">
          <h2 className="text-3xl lg:text-5xl font-extrabold leading-tight tracking-tight mb-4" data-id="element-224" data-mp="1g22tvxgyj3gz">
            We've all been there{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-kitty-yellow-400 to-orange-400" data-id="element-225" data-mp="3dvib5o4syxbr">
              …right?
            </span>
          </h2>
          <div className="flex flex-col items-center gap-3 max-w-sm mx-auto mt-6" data-id="element-226" data-mp="p0shn4cs3qqz">
            <motion.div initial={{
            opacity: 0,
            x: -12
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.2,
            duration: 0.4
          }} className="self-start flex items-end gap-2" data-id="element-227" data-mp="k2h9qz61nqbe">
              <div className="w-7 h-7 rounded-full bg-blue-500/20 flex items-center justify-center text-[10px] font-bold text-blue-400 flex-shrink-0" data-id="element-228" data-mp="17x1ujoby2b9e">
                J
              </div>
              <div className="bg-white/[0.08] border border-white/[0.1] rounded-2xl rounded-bl-md px-4 py-2.5" data-id="element-229" data-mp="1ho4ugmt50k73">
                <p className="text-sm text-gray-300" data-id="element-230" data-mp="1crxx3iet4h0r">"Who owes what?"</p>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: 12
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.4,
            duration: 0.4
          }} className="self-end flex items-end gap-2 flex-row-reverse" data-id="element-231" data-mp="1lqjf9cgd9u10">
              <div className="w-7 h-7 rounded-full bg-purple-500/20 flex items-center justify-center text-[10px] font-bold text-purple-400 flex-shrink-0" data-id="element-232" data-mp="1wav6ai4hlv6x">
                S
              </div>
              <div className="bg-white/[0.08] border border-white/[0.1] rounded-2xl rounded-br-md px-4 py-2.5" data-id="element-233" data-mp="1mksegl93qna6">
                <p className="text-sm text-gray-300" data-id="element-234" data-mp="1rdbq3co8b8cc">"I'll send it later."</p>
              </div>
            </motion.div>
            <motion.div initial={{
            opacity: 0,
            x: -12
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} transition={{
            delay: 0.6,
            duration: 0.4
          }} className="self-start flex items-end gap-2" data-id="element-235" data-mp="1clkqz58nlc9c">
              <div className="w-7 h-7 rounded-full bg-green-500/20 flex items-center justify-center text-[10px] font-bold text-green-400 flex-shrink-0" data-id="element-236" data-mp="10l39jf4e60ne">
                M
              </div>
              <div className="bg-white/[0.08] border border-white/[0.1] rounded-2xl rounded-bl-md px-4 py-2.5" data-id="element-237" data-mp="qvjnk0ldtqob">
                <p className="text-sm text-gray-300" data-id="element-238" data-mp="mu7z3ye8vbhp">"Can you remind me?"</p>
              </div>
            </motion.div>
          </div>
        </motion.div>

        {/* Pain points grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-60px'
      }} className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-239" data-mp="cvs6ts3hdw6m">
          {painPoints.map(point => <motion.div key={point.title} variants={itemVariants} className="relative rounded-2xl bg-white/[0.04] border border-white/[0.08] p-6 lg:p-7" data-id="element-240" data-mp="3w5dzuothyp6">
              <div className="w-10 h-10 rounded-xl bg-kitty-yellow-500/10 flex items-center justify-center mb-5" data-id="element-241" data-mp="228dowx3vyyu4">
                <point.icon className="w-5 h-5 text-kitty-yellow-400" data-id="element-242" data-mp="3g6zv988ta00p" />
              </div>
              <h3 className="text-lg font-bold text-white mb-2 leading-snug" data-id="element-243" data-mp="3l5uoz58bh8zl">
                {point.title}
              </h3>
              <p className="text-sm text-gray-400 leading-relaxed" data-id="element-244" data-mp="3pwb4cuvp74ol">
                {point.description}
              </p>
            </motion.div>)}
        </motion.div>

        {/* Bottom nudge */}
        <motion.p initial={{
        opacity: 0
      }} whileInView={{
        opacity: 1
      }} viewport={{
        once: true
      }} transition={{
        delay: 0.5,
        duration: 0.6
      }} className="text-center text-white text-sm mt-12" data-id="element-245" data-mp="2x8nm3mhwgq54">
          There has to be a better way.{' '}
          <span className="text-kitty-yellow-400 font-medium" data-id="element-246" data-mp="3b7x2am63vk93">
            That's what KittyPot is for.
          </span>
        </motion.p>
      </div>
    </section>;
}